import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {
  Sort,
  SortDirection,
  TableColumnComponent,
} from 'marketpro-design-lib';
import { constants } from '../../../../constants';
import { TableRegion } from '../../models/table-region.model';
import {
  DataFilters,
  DataFiltersService,
} from '../../services/data-filters.service';
import { switchMap, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { AuthService } from '../../../auth/services/auth.service';
import { StatisticsService } from '../../services/statistics.service';
import { SizeOfPrizeRanking } from '../../models/statistics.model';
import { CategoryService } from '../../services/categories.service';
import { ToggleableNameValuePair } from '../../models/name-value-pair.model';
import { flatCategories } from 'projects/marketpro-ui/src/app/utils';
import { AuthorizedResourcesService } from '../../../auth/services/authorized-resources.service';

@Component({
  selector: 'mpro-region-comparison-table',
  templateUrl: './region-comparison-table.component.html',
  styleUrls: ['./region-comparison-table.component.scss'],
})
export class RegionComparisonTableComponent
  implements AfterViewInit, OnChanges
{
  @ViewChildren(TableColumnComponent)
  private cols: QueryList<TableColumnComponent>;
  public dataFilters: DataFilters = new DataFilters();
  public isCategoriesEnabled: boolean;
  @Input() public currency: number;
  @Input() public countries: TableRegion[] = [];
  @Input() public isFromSizeOfPrize: boolean = false;
  @Input() public disabled: Set<string> = new Set();
  @Input() public selected: string;
  @Input() public selectedYear: number = constants.initialYear;
  @Input() public selectedCategory: string =
    this.dataFilters.category.toString();
  @Output() public checkedChange: EventEmitter<TableRegion> =
    new EventEmitter();
  @Output() public sortChanged: EventEmitter<Sort> = new EventEmitter();
  @Output() public selectedChanged: EventEmitter<TableRegion[]> =
    new EventEmitter();
  categoryText: string = 'All Categories';
  @Input() public currentYear: string = constants.currentYear.toString();
  public dynamicHeaderCurrentYear = `Spending in ${this.currentYear}`;
  public dynamicHeaderSelectedYear = `Spending in `;
  public isFreemium = false;

  public checked: Set<string> = new Set();
  public autoChecked: Set<string> = new Set();
  private ngUnsubscribe: Subject<void> = new Subject();
  public SortDirection = SortDirection;

  private colsByProp: Map<string, TableColumnComponent> = new Map();
  private sort: Sort = new Sort('spending_ppp', SortDirection.DESC);
  public sizeOfPrizeRanking$: Observable<SizeOfPrizeRanking[]>;
  private categories: ToggleableNameValuePair[] = [];
  public endYear: number;

  constructor(
    private elRef: ElementRef,
    private dataFiltersService: DataFiltersService,
    private authService: AuthService,
    private statisticsService: StatisticsService,
    private categoryService: CategoryService,
    public authResources: AuthorizedResourcesService
  ) {}

  public ngOnInit(): void {
    this.endYear = this.authResources.endYear;
    this.categoryService
      .getUserCategories()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((categories) => (this.categories = categories));
    if (this.isFromSizeOfPrize) {
      this.sizeOfPrizeRanking$ =
        this.statisticsService.getSizeOfPrizeRanking$.pipe(
          switchMap(() => this.statisticsService.getSizeOfPrizeRanking())
        );
    }
    this.authService.categoriesEnabled$.subscribe((isCategoriesEnabled) => {
      this.isCategoriesEnabled = isCategoriesEnabled;
    });
    this.dataFiltersService
      .getDataFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((filters) => {
        this.statisticsService.getSizeOfPrizeRanking$.next();
        this.updateCategoryTag(filters.category);
        this.dataFilters = filters;
      });
    this.authService.permissions$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((plan) => {
        this.isFreemium = plan.isFreemium;
      });
  }
  public ngAfterViewInit(): void {
    this.cols
      .filter((c) => !!c.property)
      .forEach((c) => this.colsByProp.set(c.property, c));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.selected) {
      this.checked.delete(changes.selected.previousValue);
      this.autoChecked.forEach((v) => this.checked.delete(v));
      this.autoChecked.clear();
      if (changes.selected.currentValue) {
        this.checked.add(changes.selected.currentValue);
        this.scrollToSelected(changes.selected.currentValue);
        const selectedIndex = this.countries.findIndex(
          (country) => country.code === changes.selected.currentValue
        );
        if (selectedIndex !== -1) {
          if (selectedIndex === 0) {
            this.checked.add(this.countries[1].code);
            this.autoChecked.add(this.countries[1].code);
          } else if (selectedIndex + 1 === this.countries.length) {
            this.checked.add(this.countries[selectedIndex - 1].code);
            this.autoChecked.add(this.countries[selectedIndex - 1].code);
          } else {
            this.checked.add(this.countries[selectedIndex - 1].code);
            this.checked.add(this.countries[selectedIndex + 1].code);
            this.autoChecked.add(this.countries[selectedIndex - 1].code);
            this.autoChecked.add(this.countries[selectedIndex + 1].code);
          }
        }
      } else {
        this.elRef.nativeElement.scrollTop = 0;
      }

      this.emitSelectedChanged();
    }

    if (changes.countries) {
      this.scrollToSelected(this.selected);
    }
  }

  private emitSelectedChanged(): void {
    const currentlySelected: TableRegion[] = [];
    this.checked.forEach((checked) => {
      const checkedCountry = this.countries.find(
        (country) => country.code === checked
      );
      if (checkedCountry) {
        currentlySelected.push(checkedCountry);
      }
    });
    this.selectedChanged.emit(currentlySelected);
  }

  private scrollToSelected(selectedCountryCode: string) {
    var row = document.getElementById(`region-comp-${selectedCountryCode}`);
    if (row) {
      this.elRef.nativeElement.scrollTop = row.offsetTop - row.clientHeight * 2;
    }
  }

  public checkChanged(checked: boolean, country: any) {
    this.checkedChange.emit(country);
    if (!checked) {
      this.checked.delete(country.code);
    } else {
      this.checked.add(country.code);
    }
    this.emitSelectedChanged();
  }

  public onSort(sort: Sort) {
    if (
      !this.countries ||
      this.countries.length < 1 ||
      !this.countries[0].hasOwnProperty(sort.property)
    ) {
      return;
    }

    if (this.sort && this.sort.property !== sort.property) {
      let c = this.colsByProp.get(this.sort.property);
      if (c) {
        c.sort.direction = SortDirection.NONE;
      }
    }

    if (typeof this.countries[0][sort.property] === 'string') {
      this.countries.sort((c1, c2) =>
        (c1[sort.property] as string).localeCompare(c2[sort.property] as string)
      );
    } else {
      this.countries.sort(
        (c1, c2) =>
          (c1[sort.property] as number) - (c2[sort.property] as number)
      );
    }

    if (sort.direction === SortDirection.DESC) {
      this.countries.reverse();
    }

    this.sort = sort;
    this.sortChanged.emit(this.sort);
  }

  updateCategoryTag(category) {
    this.categoryText =
      flatCategories(this.categories).find((o) => o.value === category)?.name ||
      '';
  }
}
